import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { NoProvidersIllustration } from '../images';
import { useCalendarStore } from '../stores/calendar.store';
import { CALENDAR_TRACKING_IDS } from '../trackingIds/calendar-tracking-ids';

interface Props {
  locationId: string;
  topOffset?: number;
}

export const NoProvidersLocationBanner = ({ locationId, topOffset = 0 }: Props) => {
  const { t } = useTranslation('schedule');
  const { navigate } = useSettingsNavigate();
  const { calendarEntityType } = useCalendarStore(['calendarEntityType']);
  const isOperatoryView = calendarEntityType === 'operatory';

  const handleConfigureClick = () => {
    if (isOperatoryView) {
      navigate({ to: '/schedule/operatories/:locationId', params: { locationId } });
    } else {
      navigate({ to: '/schedule/manage-providers/:locationId', params: { locationId } });
    }
  };

  return (
    <section css={styles.container} data-hide-calendar-tooltip='true'>
      <div css={[styles.content, { top: topOffset + 16 }]}>
        <NoProvidersIllustration css={{ maxHeight: 200 }} />
        <Text color='light' textAlign='center' css={{ marginTop: theme.spacing(1) }}>
          {isOperatoryView
            ? t('No operatories configured for this location.')
            : t('No providers configured for this location.')}
        </Text>
        <Button
          onClick={handleConfigureClick}
          iconName='plus'
          trackingId={CALENDAR_TRACKING_IDS.emptyCalendar.noProvidersLocationBannerButton}
        >
          {isOperatoryView ? t('Configure Operatories') : t('Configure Providers')}
        </Button>
      </div>
    </section>
  );
};

const styles = {
  container: css({
    width: '100%',
    height: '100%',
    background: theme.colors.neutral5,
  }),
  content: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(4, 2),
    position: 'sticky',
    top: 0,
  }),
};
